import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * White Classic Socks Add-to-cart handler (2026-09-11, redesigned to sit
 * inside the normal "You may also like" rail rather than a separate promo
 * block — see cart-recommendations.liquid for that row's markup/styling and
 * why its Add/"In bag" state is driven entirely by server-rendered
 * `cart.items`, never client-side JS state).
 *
 * This file's ONLY job is the ADD click: a single, guarded, completely
 * ordinary `/cart/add.js` call for exactly one unit of the authoritative
 * White Classic Socks variant — no properties, so this can NEVER become a
 * private-BOGO gift line (see brand-rep-private-bogo-gift.js's own removal
 * note — that auto-add system was deliberately deleted; this file must not
 * reintroduce anything like it). After a successful add, it dispatches the
 * same CartLinesUpdateEvent + `detail.sections` refresh convention already
 * proven in brand-rep-welcome-gift.js/brand-rep-private-bogo-gift.js, which
 * the theme's own cart-items-component/cart-drawer-component/cart-icon
 * already listen for — so the promo's Liquid re-renders with the correct
 * "In bag" state, the cart line list updates, and the header count updates,
 * all from ONE dispatch, with no page reload and no manual DOM patching
 * here.
 *
 * DUPLICATE PROTECTION: a module-scoped `addInFlight` flag ignores any
 * click received while a request is already in flight, and the clicked
 * button is disabled immediately and — on success — left disabled rather
 * than re-enabled, so even a rapid double-tap in the brief window before
 * the section refresh actually swaps this button out for the "In bag"
 * state can never fire a second add. It is only re-enabled if the add
 * itself failed, so the customer can retry.
 */

const WHITE_SOCK_VARIANT_ID = 46510415315121;

let addInFlight = false;

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

async function dispatchRefresh(result) {
  const freshCart = await fetchCart();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ id: '', quantity: 1 }],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
        detail: {
          sections: result.sections,
          items: freshCart.items,
          itemCount: freshCart.item_count,
          source: 'cart-socks-promo',
          didError: false,
        },
      }),
    })
  );
}

async function addSocks(button) {
  if (addInFlight) return;
  addInFlight = true;
  button.disabled = true;

  try {
    const sections = cartSectionIds();
    const body = JSON.stringify({
      // Deliberately NO `properties` — an ordinary merchandise line, never
      // a private-BOGO (or any other) gift-tracked line.
      items: [{ id: WHITE_SOCK_VARIANT_ID, quantity: 1 }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) {
      document.dispatchEvent(
        new CartErrorEvent({
          error: (data && (data.description || data.message)) || 'Could not add White Classic Socks — please try again.',
          code: 'INVALID',
        })
      );
      addInFlight = false;
      if (button.isConnected) button.disabled = false;
      return;
    }

    await dispatchRefresh(data);
    // Success: leave disabled — the refresh this triggers replaces this
    // button with the "In bag" state; never re-enable it, so a stray
    // double-click can never queue a second add.
    addInFlight = false;
  } catch (error) {
    console.error('[cart-socks-promo] add failed', error);
    addInFlight = false;
    if (button.isConnected) button.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-socks-promo-add]');
  if (!button) return;
  event.preventDefault();
  addSocks(button);
});
